/* eslint-disable camelcase */
/* eslint-disable no-underscore-dangle */
import { saveAs } from 'file-saver';
import BaseAPIClient from './base/BaseAPIClient';

import OrganizationModel from '../models/OrganizationModel';
import UserModel from '../models/UserModel';

import DeviceModel from '../models/DeviceModel';

let menderVersionsPromise;
let organizationsPromise;
let currentDartVersionPromise;
let allUsersPromise;
let organizationUsersPromise;

const cacheTimeout = 1800000;
const cableTesterString = 'CableTester';
export default class UserClient extends BaseAPIClient {
  getRemoteUser() {
    return this._get('accounts/me/').then((user) => new UserModel(user));
  }

  getApiVersion() {
    return this._get('version/').then((res) => res.version);
  }

  getToken(username, password) {
    return this._post('auth/login/', { username, password });
  }

  loginWithMfa(token, theCode) {
    return this._post('auth/login/code/', { ephemeral_token: token, code: theCode });
  }

  getUserMfaMethods() {
    return this._get('auth/mfa/user-active-methods/');
  }

  mfaActivateRequest(type, body) {
    if (body) {
      return this._post(`auth/${type}/activate/`, body);
    }
    return this._post(`auth/${type}/activate/`);
  }

  mfaGetUserMfaMethodsFromToken(token) {
    return this._get(`auth/${token}/mfaMethods/`);
  }

  mfaRequestCodeForNewMethod(token, method) {
    return this._post(`auth/${token}/${method}/request/`);
  }

  mfaDeactivateRequest(type) {
    return this._post(`auth/${type}/deactivate/`);
  }

  mfaMakePrimaryRequest(type) {
    return this._post(`auth/mfa/${type}/update-primary/`);
  }

  mfaActivateConfirm(type, theCode) {
    return this._post(`auth/${type}/activate/confirm/`, { code: theCode });
  }

  resetMfaForUser(userId) {
    allUsersPromise = undefined;
    return this._post(`accounts/users/${userId}/mfaReset/`);
  }

  forgotPassword(email) {
    return this._post('auth/forgot_password/', {
      email,
    });
  }

  resetPassword(token, password) {
    return this._post('auth/reset_password/', {
      token,
      password,
    });
  }

  resetUser(id) {
    return this._post(`accounts/users/${id}/reset/`);
  }

  inviteUser(
    email,
    is_admin,
    full_name,
    phone_number,
    phone_country_code,
    phone_number_e164,
    is_organization_admin,
    organization_id,
    allowed_to_remove_files,
    allowed_to_access_management,
    allowed_programmatic_access,
    is_site_admin,
    sites,
  ) {
    allUsersPromise = undefined;
    organizationUsersPromise = undefined;
    return this._post('accounts/invite_user/', {
      email,
      is_admin,
      full_name,
      phone_number,
      phone_country_code,
      phone_number_e164,
      is_organization_admin,
      organization_id,
      allowed_to_remove_files,
      allowed_to_access_management,
      allowed_programmatic_access,
      is_site_admin,
      sites,
    });
  }

  verifyUser(token, password) {
    return this._post('accounts/verify_email/', {
      token,
      password,
    });
  }

  updateUser(
    id,
    email,
    is_admin,
    full_name,
    phone_number,
    phone_country_code,
    phone_number_e164,
    is_organization_admin,
    organization_id,
    allowed_to_remove_files,
    allowed_to_access_management,
    allowed_programmatic_access,
    is_site_admin,
    sites,
  ) {
    allUsersPromise = undefined;
    organizationUsersPromise = undefined;
    return this._patch(`accounts/users/${id}/`, {
      email,
      is_admin,
      full_name,
      phone_number,
      phone_country_code,
      phone_number_e164,
      is_organization_admin,
      organization_id,
      allowed_to_remove_files,
      allowed_to_access_management,
      allowed_programmatic_access,
      is_site_admin,
    }).then(() => this._post(`accounts/users/${id}/sites/`, { sites }));
  }

  getUsers() {
    if (!allUsersPromise) {
      allUsersPromise = this._get('accounts/users/', {
        limit: 9999,
      }).then((res) => res.results.map((r) => new UserModel(r)));
      setTimeout(() => {
        allUsersPromise = null;
      }, cacheTimeout);
    }
    return allUsersPromise;
  }

  getOrganizations() {
    if (!organizationsPromise) {
      organizationsPromise = this._get('organizations/', {
        limit: 9999,
      }).then((res) => res.results
        .map((r) => new OrganizationModel(r))
        .sort((a, b) => a.name.localeCompare(b.name)));
      setTimeout(() => {
        organizationsPromise = null;
      }, cacheTimeout);
    }
    return organizationsPromise;
  }

  getSitesForOrganization(id) {
    return this._get(`organizations/${id}/sites`, {
      limit: 9999,
    }).then((res) => res.results.sort((a, b) => a.name.localeCompare(b.name)));
  }

  createSite(orgId, data) {
    return this._post(`organizations/${orgId}/sites`, data);
  }

  updateSitesForOrganization(orgId, siteId, data) {
    return this._patch(`organizations/${orgId}/sites/${siteId}`, data);
  }

  getAllSites(allowDeleted = false) {
    return this._get('organizations/sites', {
      limit: 9999,
      allow_deleted: allowDeleted ? true : undefined,
    }).then((res) => res.results);
  }

  getSitesViewData() {
    return Promise.all([
      this._get('organizations/sites', {
        limit: 9999,
      }).then((res) => res.results),
      this._get('organizations/', {
        limit: 9999,
        primary: true,
      }).then((res) => res.results),
    ])
      .then(([sitesResult, organizations]) => [
        ...organizations
          .filter((org) => !org.is_removed)
          .map((org) => ({
            id: `org${org.id}`,
            name: 'Primary',
            organization: {
              id: org.id,
              name: org.name,
            },
            device_count: org.number_of_devices,
            contact_user: {
              email: org.email_address,
              phone_country_code: org.phone_country_code,
              phone_number: org.phone_number,
              phone_number_e164: org.phone_number_e164,
            },
            isPrimary: true,
          })), ...sitesResult]
        .sort((a, b) => a.organization.name.localeCompare(b.organization.name)));
  }

  removeSite(organizationId, id) {
    return this._delete(`organizations/${organizationId}/sites/${id}`);
  }

  getOrganizationUsers(organizationId) {
    if (!organizationUsersPromise) {
      organizationUsersPromise = this._get(`organizations/${organizationId}/users`, {
        limit: 9999,
      }).then((res) => res.results);
      setTimeout(() => {
        organizationUsersPromise = null;
      }, cacheTimeout);
    }
    return organizationUsersPromise;
  }

  createOrganization(
    name,
    email_address,
    phone_number,
    phone_country_code,
    phone_number_e164,
    allowed_to_remove_files,
    kgb_admin_allowed_download_of_files,
    is_allowed_demo_access,
    is_allowed_data_encryption,
    dart_software_version,
  ) {
    organizationsPromise = undefined;
    return this._post('organizations/', {
      name,
      email_address,
      phone_number,
      phone_country_code,
      phone_number_e164,
      allowed_to_remove_files,
      kgb_admin_allowed_download_of_files,
      is_allowed_demo_access,
      is_allowed_data_encryption,
      dart_software_version,
    });
  }

  updateOrganization(
    id,
    name,
    email_address,
    phone_number,
    phone_country_code,
    phone_number_e164,
    allowed_to_remove_files,
    kgb_admin_allowed_download_of_files,
    is_allowed_demo_access,
    is_allowed_data_encryption,
    dart_software_version,
  ) {
    organizationsPromise = undefined;
    return this._patch(`organizations/${id}/`, {
      name,
      email_address,
      phone_number,
      phone_country_code,
      phone_number_e164,
      allowed_to_remove_files,
      kgb_admin_allowed_download_of_files,
      is_allowed_demo_access,
      is_allowed_data_encryption,
      dart_software_version,
    });
  }

  getOrganizationKeys(organizationId) {
    return this._get(`organizations/${organizationId}/keys/`, {
      limit: 9999,
    }).then((res) => res.results);
  }

  addOrganizationEncryption(organizationId, data) {
    return this._post(`organizations/${organizationId}/keys/`, data);
  }

  removeOrganizationEncryption(organizationId, keyId) {
    return this._delete(`organizations/${organizationId}/keys/${keyId}/`);
  }

  getDeviceEncryptionKeys(organizationId, deviceId) {
    return this._get(`devices/${organizationId}/${deviceId}/keys/`, {
      limit: 9999,
    }).then((res) => res.results);
  }

  getDevices(selectedOrg) {
    const options = {
      limit: 9999,
    };

    if (selectedOrg) {
      options.organization_id = selectedOrg;
    }

    return this._get('devices/', options)
      .then((res) => res.results
        .sort((a, b) => a.serial_number.localeCompare(b.serial_number))
        .map((r) => new DeviceModel(r)));
  }

  getDeviceDownloads(id) {
    return this._get(`devices/${id}/telemetry/`, {
      limit: 9999,
    });
  }

  getDeviceCheckins(id) {
    return this._get(`devices/${id}/checkins/`);
  }

  getDeviceHistory(id) {
    return this._get(`devices/${id}/history/`, {
      limit: 9999,
    }).then((res) => res.results);
  }

  getMenderVersions() {
    if (!menderVersionsPromise) {
      menderVersionsPromise = this._get('management/menderVersions/');
      setTimeout(() => {
        menderVersionsPromise = null;
      }, cacheTimeout);
    }
    return menderVersionsPromise;
  }

  getDartVersionOptions() {
    return this.getMenderVersions()
      .then((x) => x.filter((f) => !f.name.includes('-ENG') && !f.name.includes(cableTesterString)));
  }

  geCableTesterOptions() {
    return this.getMenderVersions()
      .then((x) => x.filter((f) => f.name.includes(cableTesterString)));
  }

  getCurrentMenderVersion() {
    if (!currentDartVersionPromise) {
      currentDartVersionPromise = this._get('management/dartVersion/');
      setTimeout(() => {
        currentDartVersionPromise = null;
      }, cacheTimeout);
    }
    return currentDartVersionPromise;
  }

  updateMenderVersion(name) {
    currentDartVersionPromise = undefined;
    organizationsPromise = undefined;
    return this._post('management/dartVersion/', { name });
  }

  getCurrentMenderTestFixtureVersion() {
    return this._get('management/testFixtureSoftware/');
  }

  updateMenderTestFixtureVersion(name) {
    return this._post('management/testFixtureSoftware/', { name });
  }

  updateToLatestVersion(deviceId) {
    return this._post(`devices/${deviceId}/update/`, {});
  }

  checkIfUpdateInitiated(deviceId) {
    return this._get(`devices/${deviceId}/update/status`);
  }

  downloadFile(deviceId, filename) {
    return this._get(`devices/${deviceId}/telemetry/download`, {
      filename,
    }).then((resp) => {
      if (resp.signed_s3_url) {
        window.location = resp.signed_s3_url;
      }
    });
  }

  getReporting() {
    return this._get('reporting/', {
      limit: 9999,
    }).then((res) => res.results);
  }

  updateReport(id, data) {
    if (id >= 0) {
      return this._put(`reporting/${id}/`, data);
    }
    return this._post('reporting/', data);
  }

  removeReport(id) {
    return this._delete(`reporting/${id}`);
  }

  createDevice(data) {
    return this._post('devices/', data);
  }

  updateDevice(id, data) {
    organizationsPromise = undefined;
    return this._patch(`devices/${id}/`, data);
  }

  disableDevice(deviceId) {
    organizationsPromise = undefined;
    return this._patch(`devices/${deviceId}/`, {
      disabled: true,
    });
  }

  enableDevice(deviceId) {
    return this._patch(`devices/${deviceId}/`, {
      disabled: false,
    });
  }

  getDeviceData(deviceId) {
    return this._get(`devices/${deviceId}/data/`);
  }

  findOrgs(query) {
    return this._get(`organizations/search/?search=${query}`);
  }

  getDownloadHistory(deviceId, downloadId) {
    return this._get(`devices/${deviceId}/telemetry/${downloadId}/downloads`, { limit: 9999 }).then((x) => x.results);
  }

  deleteDevice(deviceId) {
    return this._delete(`devices/${deviceId}/`);
  }

  deleteOrg(id) {
    organizationsPromise = undefined;
    return this._delete(`organizations/${id}/`);
  }

  enableOrg(id) {
    organizationsPromise = undefined;
    return this._put(`organizations/${id}/enable`);
  }

  deleteUser(id) {
    allUsersPromise = undefined;
    organizationUsersPromise = undefined;
    return this._delete(`accounts/users/${id}/`);
  }

  deleteFile(deviceId, fileName) {
    return this._delete(`devices/${deviceId}/telemetry?filename=${fileName}`);
  }

  changePassword(oldPassword, password) {
    return this._post('accounts/password/', {
      oldPassword,
      password,
    });
  }

  updateProfile(full_name, phone_number, phone_country_code, phone_number_e164) {
    return this._patch('accounts/me/', {
      full_name,
      phone_number,
      phone_country_code,
      phone_number_e164,
    });
  }

  createUserPublicUserKey(data) {
    return this._post('accounts/accessKeys/', data);
  }

  getUserPublicKeys() {
    return this._get('accounts/accessKeys/', { limit: 9999 }).then((x) => x.results);
  }

  getUserPublicKeysForUser(userId) {
    return this._get(`accounts/users/${userId}/accessKeys/`, { limit: 9999 }).then((x) => x.results);
  }

  removeUserKey(keyId) {
    return this._delete(`accounts/accessKeys/${keyId}/`);
  }

  removeUserKeyForUser(userId, keyId) {
    return this._delete(`accounts/users/${userId}/accessKeys/${keyId}/`);
  }

  regenerateUserKey(keyId) {
    return this._post(`accounts/accessKeys/${keyId}/regenerate/`);
  }

  /**
   * Get device types
   * @returns List of device usage information
   */
  getDeviceTypes() {
    return this._get('devices/types/');
  }

  updateAllDeviceSoftwareVersions() {
    return this._post('devices/populateAllVersions');
  }

  /**
   * Get device usage for items
   * @param {*} options Options for the usage data
   * @returns List of device usage information
   */
  getDeviceUsage(options) {
    const requestOptions = {
      ...options,
      limit: 999999,
    };

    return this._get('devices/usages/', requestOptions);
  }

  updateUsageData(usageData) {
    return this._patch(`devices/usages/${usageData.id}/`, usageData);
  }

  getDeviceUsageChangeLog(usageId) {
    return this._get(`devices/usages/${usageId}/changes`);
  }

  getExportedData(options) {
    let filename = 'download-file';
    return this.fetchWithParams('get', 'devices/usages/export', options)
      .then((res) => {
        const header = res.headers.get('Content-Disposition');
        const parts = header.split(';');
        if (parts) {
          filename = parts[1].split('=')[1].trim().replaceAll('"', '');
        }

        return res.blob();
      })
      .then((blob) => {
        saveAs(blob, filename);
      });
  }

  getTestResults(options) {
    const requestOptions = {
      ...options,
      limit: 999999,
    };

    return this._get('testResults/', requestOptions);
  }

  getTestResultExportedData(options) {
    let filename = 'test-results-export';
    return this.fetchWithParams('get', 'testResults/export', options)
      .then((res) => {
        const header = res.headers.get('Content-Disposition');
        const parts = header.split(';');
        if (parts) {
          filename = parts[1].split('=')[1].trim().replaceAll('"', '');
        }

        return res.blob();
      })
      .then((blob) => {
        saveAs(blob, filename);
      });
  }

  downloadTestResultFile(testResultId) {
    return this._get(`testResults/${testResultId}/download`).then((resp) => {
      if (resp.length > 0) {
        window.location = resp;
      }
    });
  }

  getSwaggerDoc() {
    return this._get('swagger.json/');
  }

  getTestFixtures(selectedOrg) {
    const options = {
      limit: 9999,
    };

    if (selectedOrg) {
      options.organizationId = selectedOrg;
    }

    return this._get('testFixtures/', options).then((res) => res.results);
  }

  shouldShowTestFixture() {
    return this._get('testFixtures/show');
  }

  createTestFixture(data) {
    return this._post('testFixtures/', data);
  }

  updateTestFixture(id, data) {
    return this._patch(`testFixtures/${id}/`, data);
  }

  enableTestFixture(id) {
    return this._patch(`testFixtures/${id}/`, { disabled: false });
  }

  getTestFixtureCheckins(id) {
    return this._get(`testFixtures/${id}/checkins/`);
  }

  updateTestFixtureToLatestSoftwareVersion(id) {
    return this._post(`testFixtures/${id}/update/`, {});
  }

  getTestFixtureResults(options) {
    const requestOptions = {
      ...options,
      limit: 999999,
    };

    return this._get('testFixtures/results', requestOptions);
  }

  getTestFixtureResultExportedData(options) {
    let filename = 'cable-test-results-export';
    return this.fetchWithParams('get', 'testFixtures/results/export', options)
      .then((res) => {
        const header = res.headers.get('Content-Disposition');
        const parts = header.split(';');
        if (parts) {
          filename = parts[1].split('=')[1].trim().replaceAll('"', '');
        }

        return res.blob();
      })
      .then((blob) => {
        saveAs(blob, filename);
      });
  }

  downloadTestFixtureResultFile(testFixtureResultId) {
    return this._get(`testFixtures/results/${testFixtureResultId}/download`).then((resp) => {
      if (resp.length > 0) {
        window.location = resp;
      }
    });
  }

  updateAllTestFixtureSoftwareVersions() {
    return this._post('testFixtures/populateAllVersions');
  }
}
